import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  ArcElement, PointElement, LineElement, Filler, Title, Tooltip, Legend,
} from 'chart.js';
import {
  MdDirectionsCar, MdMeetingRoom, MdSupportAgent, MdWork,
  MdRefresh, MdCalendarToday, MdTrendingUp, MdTrendingDown,
  MdArrowUpward, MdArrowDownward, MdFilterList, MdDownload, MdBadge,
} from 'react-icons/md';
import monthlyReportsApi from '../../../api/monthlyReportsApi';
import './MonthlyReports.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement,
  PointElement, LineElement, Filler, Title, Tooltip, Legend);

const TABS = [
  { id: 'vehicle',   label: 'Vehicle Management', icon: <MdDirectionsCar /> },
  { id: 'workspace', label: 'Workspace Booking',  icon: <MdMeetingRoom />   },
  { id: 'helpdesk',  label: 'Helpdesk',           icon: <MdSupportAgent />  },
  { id: 'job',       label: 'Job Management',     icon: <MdWork />          },
  { id: 'idcard',    label: 'ID Card History',    icon: <MdBadge />         },
];


const CHART_COLORS = [
  '#704493','#2196F3','#FF9800','#4CAF50','#F44336',
  '#9C27B0','#00BCD4','#607D8B','#E91E63','#3F51B5',
  '#009688','#FF5722','#795548','#CDDC39','#03A9F4',
];

const STATUS_COLORS = {
  APPROVED: '#4CAF50', REJECTED: '#F44336', CANCELLED: '#607D8B',
  AUTO_CANCELLED: '#9E9E9E', PENDING: '#FF9800',
  OPEN: '#2196F3', ASSIGNED: '#704493', IN_PROGRESS: '#FF9800',
  RESOLVED: '#4CAF50', CLOSED: '#607D8B', REOPENED: '#F44336',
  LOW: '#4CAF50', MEDIUM: '#FF9800', HIGH: '#F44336', CRITICAL: '#9C27B0',
  SOURCED: '#2196F3', SCREENING: '#FF9800', INTERVIEWED: '#9C27B0',
  OFFERED: '#00BCD4', JOINED: '#4CAF50', REJECTED_STAGE: '#F44336',
};

const CHART_OPTS = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } } },
};
const BAR_OPTS = {
  ...CHART_OPTS,
  scales: { x: { grid: { display: false } }, y: { grid: { color: '#f0f0f0' }, beginAtZero: true } },
};
const DOUGHNUT_OPTS = {
  ...CHART_OPTS, cutout: '65%',
  plugins: { ...CHART_OPTS.plugins, tooltip: { callbacks: {
    label: ctx => ` ${ctx.label}: ${ctx.formattedValue}`,
  }}},
};

function delta(current, previous) {
  if (!previous || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

function KpiCard({ label, value, sub, prev, color, icon }) {
  const pct = prev !== undefined && prev !== null ? delta(Number(String(value).replace('%','')), prev) : null;
  return (
    <div className="mr-kpi-card" style={{ '--kpi-color': color || '#704493' }}>
      <div className="mr-kpi-icon">{icon}</div>
      <div className="mr-kpi-body">
        <div className="mr-kpi-value">{value ?? '—'}</div>
        <div className="mr-kpi-label">{label}</div>
        {sub  && <div className="mr-kpi-sub">{sub}</div>}
        {pct !== null && (
          <div className={`mr-kpi-delta ${pct >= 0 ? 'up' : 'down'}`}>
            {pct >= 0 ? <MdArrowUpward /> : <MdArrowDownward />}
            {Math.abs(pct)}% vs last month
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Utilization Banner — hero metric showing how much the module is being used ───
function UtilizationBanner({ rate, headline, detail, secondaryRate, secondaryLabel }) {
  const r = Math.min(Number(rate) || 0, 100);
  const color = r >= 70 ? '#4CAF50' : r >= 40 ? '#FF9800' : r > 0 ? '#F44336' : '#94a3b8';
  // SVG half-gauge: arc from 180° to 0°, radius 36, centre (50,50), length = π*36 ≈ 113
  const arcLen   = Math.PI * 36;
  const dashFill = (r / 100) * arcLen;
  return (
    <div className="mr-util-banner">
      <div className="mr-util-gauge-wrap">
        <svg viewBox="0 0 100 54" className="mr-util-svg">
          <path d="M 7 50 A 36 36 0 0 1 93 50" stroke="#e2e8f0" strokeWidth="9" fill="none" strokeLinecap="round"/>
          <path d="M 7 50 A 36 36 0 0 1 93 50" stroke={color}   strokeWidth="9" fill="none" strokeLinecap="round"
            strokeDasharray={`${dashFill} ${arcLen}`}
            style={{ transition: 'stroke-dasharray 0.9s ease' }}/>
        </svg>
        <div className="mr-util-pct" style={{ color }}>{r}%</div>
      </div>
      <div className="mr-util-body">
        <div className="mr-util-headline">{headline}</div>
        <div className="mr-util-detail">{detail}</div>
        <div className="mr-util-bar-track">
          <div className="mr-util-bar-fill" style={{ width: `${r}%`, background: color }}/>
        </div>
        {secondaryRate !== undefined && secondaryRate !== null && (
          <div className="mr-util-secondary">
            <span className="mr-util-sec-val" style={{ color: secondaryRate >= 70 ? '#4CAF50' : secondaryRate >= 40 ? '#FF9800' : '#F44336' }}>{secondaryRate}%</span>
            <span className="mr-util-sec-label">{secondaryLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CSV Download helper ───
function downloadCSV(filename, rows, headers) {
  const lines = [headers.join(','), ...rows.map(r => headers.map(h => {
    const v = r[h] ?? '';
    return typeof v === 'string' && v.includes(',') ? `"${v}"` : v;
  }).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
// VEHICLE TAB
// ─────────────────────────────────────────────────────────────────────────────
function VehicleTab({ data }) {
  const [expandedEmp, setExpandedEmp] = useState(null);

  if (!data || Object.keys(data).length === 0)
    return <div className="mr-empty">No vehicle data available for this period.</div>;

  const prev = data.prevMonth || {};

  const yearChart = {
    labels: (data.yearTrend || []).map(r => r.month),
    datasets: [
      { label: 'Morning (AM)', data: (data.yearTrend || []).map(r => r.morning), backgroundColor: '#704493', borderRadius: 4 },
      { label: 'Evening (PM)', data: (data.yearTrend || []).map(r => r.evening), backgroundColor: '#2196F3', borderRadius: 4 },
    ],
  };

  const dailyChart = {
    labels: (data.dailyTrend || []).map(r => r.day),
    datasets: [
      { label: 'Morning', data: (data.dailyTrend || []).map(r => r.morning), borderColor: '#704493', backgroundColor: 'rgba(112,68,147,0.1)', fill: true, tension: 0.4, pointRadius: 3 },
      { label: 'Evening', data: (data.dailyTrend || []).map(r => r.evening), borderColor: '#2196F3', backgroundColor: 'rgba(33,150,243,0.1)', fill: true, tension: 0.4, pointRadius: 3 },
    ],
  };

  const topRoutes = (data.routeBreakdown || []).slice(0, 10);
  const routeChart = {
    labels: topRoutes.map(r => r.route.length > 28 ? r.route.substring(0, 28) + '…' : r.route),
    datasets: [
      { label: 'Morning', data: topRoutes.map(r => r.morning), backgroundColor: '#704493', borderRadius: 4 },
      { label: 'Evening', data: topRoutes.map(r => r.evening), backgroundColor: '#2196F3', borderRadius: 4 },
    ],
  };

  const gatemanData = (data.gatemanBreakdown || []).slice(0, 8);
  const gatemanChart = {
    labels: gatemanData.map(g => g.name),
    datasets: [{ label: 'Scans', data: gatemanData.map(g => g.scans), backgroundColor: CHART_COLORS.slice(0, gatemanData.length), borderRadius: 4 }],
  };

  const handleDownloadRoutes = () => {
    downloadCSV(
      `vehicle-routes-${data.period?.replace(' ', '-')}.csv`,
      data.routeBreakdown || [],
      ['route', 'morning', 'evening', 'total']
    );
  };

  const handleDownloadUsers = () => {
    const rows = (data.userScanDetails || []).map(u => ({
      empId:        u.empId,
      name:         u.name,
      totalScans:   u.totalScans,
      morningScans: u.morningScans,
      eveningScans: u.eveningScans,
      routes:       (u.routes || []).map(r => `${r.route}(${r.total})`).join(' | '),
      scannedBy:    (u.gatemen || []).map(g => `${g.name}(${g.scans})`).join(' | '),
    }));
    downloadCSV(
      `vehicle-employees-${data.period?.replace(' ', '-')}.csv`,
      rows,
      ['empId', 'name', 'totalScans', 'morningScans', 'eveningScans', 'routes', 'scannedBy']
    );
  };

  return (
    <div className="mr-tab-content">
      <UtilizationBanner
        rate={data.utilizationRate ?? 0}
        headline="Vehicle Management Module Utilization"
        detail={`${data.uniqueEmployees ?? 0} out of ${data.totalRegisteredUsers ?? '—'} registered employees used this module in ${data.period}`}
        secondaryRate={data.totalScans > 0 ? Math.round(data.morningScans * 100 / data.totalScans) : 0}
        secondaryLabel="of scans are morning (AM) trips"
      />
      <div className="mr-kpi-grid">
        <KpiCard label="Active Employees" value={data.uniqueEmployees} prev={prev.uniqueEmployees} color="#704493" icon={<MdDirectionsCar />} />
        <KpiCard label="Total Scans"      value={data.totalScans}      prev={prev.totalScans}      color="#2196F3" icon={<MdTrendingUp />} />
        <KpiCard label="Morning (AM)"     value={data.morningScans}    prev={prev.morningScans}    color="#FF9800" icon={<MdCalendarToday />} />
        <KpiCard label="Evening (PM)"     value={data.eveningScans}    prev={prev.eveningScans}    color="#4CAF50" icon={<MdCalendarToday />} />
      </div>

      <div className="mr-card mr-card-full">
        <div className="mr-card-header">
          <h3>Year Overview — {data.year}</h3>
        </div>
        <div className="mr-chart-wrap" style={{ height: 260 }}>
          <Bar data={yearChart} options={BAR_OPTS} />
        </div>
      </div>

      <div className="mr-card mr-card-full">
        <h3>Daily Trend — {data.period}</h3>
        <div className="mr-chart-wrap" style={{ height: 240 }}>
          <Line data={dailyChart} options={BAR_OPTS} />
        </div>
      </div>

      <div className="mr-chart-grid">
        <div className="mr-card">
          <h3>Top Routes by Scans</h3>
          <div className="mr-chart-wrap" style={{ height: 300 }}>
            <Bar data={routeChart} options={{ ...BAR_OPTS, indexAxis: 'y', scales: { x: { grid: { color: '#f0f0f0' }, beginAtZero: true }, y: { grid: { display: false }, ticks: { font: { size: 11 } } } } }} />
          </div>
        </div>
        <div className="mr-card">
          <h3>Scans by Gateman</h3>
          <div className="mr-chart-wrap" style={{ height: 300 }}>
            <Bar data={gatemanChart} options={{ ...BAR_OPTS, indexAxis: 'y', scales: { x: { grid: { color: '#f0f0f0' }, beginAtZero: true }, y: { grid: { display: false } } } }} />
          </div>
        </div>
      </div>

      <div className="mr-card mr-card-full">
        <div className="mr-card-header">
          <h3>Route Breakdown Details</h3>
          <button className="mr-btn-download" onClick={handleDownloadRoutes}><MdDownload /> Download CSV</button>
        </div>
        <div className="mr-table-wrap">
          <table className="mr-table">
            <thead>
              <tr><th>#</th><th>Route</th><th>Location</th><th>Morning</th><th>Evening</th><th>Total Scans</th><th>Route Share</th></tr>
            </thead>
            <tbody>
              {(data.routeBreakdown || []).map((r, i) => {
                const totalAllRoutes = (data.routeBreakdown || []).reduce((s, x) => s + x.total, 0);
                const share = totalAllRoutes > 0 ? Math.round(r.total * 100 / totalAllRoutes) : 0;
                return (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td className="mr-td-route">{r.route}</td>
                  <td><span className="mr-location-badge">{r.location || '—'}</span></td>
                  <td><span className="mr-badge mr-badge-am">{r.morning}</span></td>
                  <td><span className="mr-badge mr-badge-pm">{r.evening}</span></td>
                  <td><strong>{r.total}</strong></td>
                  <td>
                    <div className="mr-progress-wrap">
                      <div className="mr-progress-bar" style={{ width: `${share}%` }} />
                      <span>{share}%</span>
                    </div>
                  </td>
                </tr>
              )})}
              {!(data.routeBreakdown || []).length && <tr><td colSpan="7" className="mr-td-empty">No route data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Employee Scan Details ── */}
      <div className="mr-card mr-card-full">
        <div className="mr-card-header">
          <h3>Employee Scan Details</h3>
          <span className="mr-card-sub">{(data.userScanDetails || []).length} unique employees</span>
          <button className="mr-btn-download" onClick={handleDownloadUsers}><MdDownload /> Download CSV</button>
        </div>
        <div className="mr-table-wrap">
          <table className="mr-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Emp ID</th>
                <th>Name</th>
                <th>Location</th>
                <th>Total Scans</th>
                <th>AM</th>
                <th>PM</th>
                <th>Routes Used</th>
                <th>Scanned By</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(data.userScanDetails || []).map((u, i) => {
                const isOpen = expandedEmp === u.empId;
                return (
                  <React.Fragment key={u.empId}>
                    <tr className={isOpen ? 'mr-tr-expanded' : ''}>
                      <td>{i + 1}</td>
                      <td><code className="mr-empid">{u.empId}</code></td>
                      <td><strong>{u.name}</strong></td>
                      <td><span className="mr-location-badge">{u.location || '—'}</span></td>
                      <td><strong style={{ color: '#704493' }}>{u.totalScans}</strong></td>
                      <td><span className="mr-badge mr-badge-am">{u.morningScans}</span></td>
                      <td><span className="mr-badge mr-badge-pm">{u.eveningScans}</span></td>
                      <td className="mr-td-routes-cell">
                        {(u.routes || []).slice(0, 2).map((r, ri) => (
                          <span key={ri} className="mr-route-pill">{r.route} <b>×{r.total}</b></span>
                        ))}
                        {(u.routes || []).length > 2 && <span className="mr-route-more">+{u.routes.length - 2} more</span>}
                      </td>
                      <td className="mr-td-gatemen-cell">
                        {(u.gatemen || []).map((g, gi) => (
                          <span key={gi} className="mr-gateman-pill">{g.name} <b>({g.scans})</b></span>
                        ))}
                      </td>
                      <td>
                        {(u.routes || []).length > 0 && (
                          <button className="mr-expand-btn" onClick={() => setExpandedEmp(isOpen ? null : u.empId)}>
                            {isOpen ? '▲' : '▼'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="mr-tr-detail-row">
                        <td colSpan="10">
                          <div className="mr-emp-detail-box">
                            <div className="mr-emp-detail-section">
                              <div className="mr-emp-detail-title">All Routes for {u.name}</div>
                              <table className="mr-inner-table">
                                <thead><tr><th>Route</th><th>Morning</th><th>Evening</th><th>Total</th></tr></thead>
                                <tbody>
                                  {(u.routes || []).map((r, ri) => (
                                    <tr key={ri}>
                                      <td>{r.route}</td>
                                      <td><span className="mr-badge mr-badge-am">{r.morning}</span></td>
                                      <td><span className="mr-badge mr-badge-pm">{r.evening}</span></td>
                                      <td><strong>{r.total}</strong></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!(data.userScanDetails || []).length && (
                <tr><td colSpan="10" className="mr-td-empty">No employee scan data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WORKSPACE TAB
// ─────────────────────────────────────────────────────────────────────────────
function WorkspaceTab({ data }) {
  if (!data || Object.keys(data).length === 0)
    return <div className="mr-empty">No workspace data available for this period.</div>;

  const prev = data.prevMonth || {};

  const yearChart = {
    labels: (data.yearTrend || []).map(r => r.month),
    datasets: [
      { label: 'Approved', data: (data.yearTrend || []).map(r => r.approved), backgroundColor: '#4CAF50', borderRadius: 4 },
      { label: 'Rejected', data: (data.yearTrend || []).map(r => r.rejected), backgroundColor: '#F44336', borderRadius: 4 },
      { label: 'Other',    data: (data.yearTrend || []).map(r => Math.max(0, r.total - r.approved - r.rejected)), backgroundColor: '#607D8B', borderRadius: 4 },
    ],
  };

  const dailyChart = {
    labels: (data.dailyTrend || []).map(r => r.day),
    datasets: [
      { label: 'Total',    data: (data.dailyTrend || []).map(r => r.total),    borderColor: '#704493', backgroundColor: 'rgba(112,68,147,0.1)', fill: true, tension: 0.4, pointRadius: 3 },
      { label: 'Approved', data: (data.dailyTrend || []).map(r => r.approved), borderColor: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.08)', fill: false, tension: 0.4, pointRadius: 3 },
    ],
  };

  const statusEntries = Object.entries(data.statusBreakdown || {});
  const statusChart = { labels: statusEntries.map(([k]) => k), datasets: [{ data: statusEntries.map(([, v]) => v), backgroundColor: statusEntries.map(([k]) => STATUS_COLORS[k] || '#9E9E9E'), borderWidth: 2, borderColor: '#fff' }] };

  const typeEntries = Object.entries(data.roomTypeBreakdown || {});
  const typeChart = { labels: typeEntries.map(([k]) => k), datasets: [{ data: typeEntries.map(([, v]) => v), backgroundColor: CHART_COLORS.slice(0, typeEntries.length), borderWidth: 2, borderColor: '#fff' }] };

  const locEntries = Object.entries(data.locationBreakdown || {});
  const locChart = { labels: locEntries.map(([k]) => k), datasets: [{ data: locEntries.map(([, v]) => v), backgroundColor: CHART_COLORS.slice(0, locEntries.length), borderWidth: 2, borderColor: '#fff' }] };

  const topRooms = (data.topRooms || []).slice(0, 10);
  const roomChart = { labels: topRooms.map(r => r.roomName.length > 20 ? r.roomName.substring(0, 20) + '…' : r.roomName), datasets: [{ label: 'Bookings', data: topRooms.map(r => r.bookings), backgroundColor: '#704493', borderRadius: 4 }] };

  const hourEntries = Object.entries(data.peakHours || {}).sort(([a], [b]) => a.localeCompare(b));
  const hourChart = { labels: hourEntries.map(([k]) => k), datasets: [{ label: 'Bookings', data: hourEntries.map(([, v]) => v), backgroundColor: 'rgba(112,68,147,0.75)', borderRadius: 4, borderColor: '#704493', borderWidth: 1 }] };

  const handleDownload = () => {
    downloadCSV(`workspace-report-${data.period?.replace(' ', '-')}.csv`, data.topRooms || [], ['roomName', 'roomType', 'location', 'tower', 'floor', 'bookings']);
  };

  return (
    <div className="mr-tab-content">
      <UtilizationBanner
        rate={data.roomUtilizationRate ?? 0}
        headline="Workspace Booking Module Utilization"
        detail={`${data.activeRooms ?? 0} of ${data.totalRooms ?? '—'} conference rooms booked at least once in ${data.period}`}
        secondaryRate={data.userUtilizationRate ?? 0}
        secondaryLabel="of registered employees made at least one booking"
      />
      <div className="mr-kpi-grid">
        <KpiCard label="Total Bookings"  value={data.totalBookings}       prev={prev.totalBookings}  color="#704493" icon={<MdMeetingRoom />} />
        <KpiCard label="Approval Rate"   value={`${data.approvalRate ?? 0}%`}  sub={prev.approvalRate !== undefined ? `Prev: ${prev.approvalRate}%` : ''} color="#4CAF50" icon={<MdTrendingUp />} />
        <KpiCard label="Avg Duration"    value={`${data.avgDurationMinutes ?? 0} min`} color="#FF9800"   icon={<MdCalendarToday />} />
        <KpiCard label="Unique Users"    value={data.uniqueUsers}         prev={prev.uniqueUsers}    color="#2196F3" icon={<MdTrendingUp />} />
      </div>
      <div className="mr-kpi-grid mr-kpi-grid-5">
        <div className="mr-stat-chip mr-chip-green">  <span>{data.approvedBookings}</span>  <small>Approved</small></div>
        <div className="mr-stat-chip mr-chip-red">    <span>{data.rejectedBookings}</span>  <small>Rejected</small></div>
        <div className="mr-stat-chip mr-chip-grey">   <span>{data.cancelledBookings}</span> <small>Cancelled</small></div>
        <div className="mr-stat-chip mr-chip-orange"> <span>{data.pendingBookings}</span>   <small>Pending</small></div>
        <div className="mr-stat-chip mr-chip-blue">   <span>{data.totalAttendees}</span>    <small>Attendees</small></div>
      </div>
      <div className="mr-card mr-card-full">
        <h3>Year Overview — {data.year}</h3>
        <div className="mr-chart-wrap" style={{ height: 260 }}>
          <Bar data={yearChart} options={{ ...BAR_OPTS, scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, beginAtZero: true, grid: { color: '#f0f0f0' } } } }} />
        </div>
      </div>
      <div className="mr-card mr-card-full">
        <h3>Daily Trend — {data.period}</h3>
        <div className="mr-chart-wrap" style={{ height: 230 }}>
          <Line data={dailyChart} options={BAR_OPTS} />
        </div>
      </div>
      <div className="mr-chart-grid mr-chart-grid-3">
        <div className="mr-card"><h3>By Status</h3><div className="mr-chart-wrap" style={{ height: 240 }}><Doughnut data={statusChart} options={DOUGHNUT_OPTS} /></div></div>
        <div className="mr-card"><h3>By Room Type</h3><div className="mr-chart-wrap" style={{ height: 240 }}><Doughnut data={typeChart} options={DOUGHNUT_OPTS} /></div></div>
        <div className="mr-card"><h3>By Location</h3><div className="mr-chart-wrap" style={{ height: 240 }}><Doughnut data={locChart} options={DOUGHNUT_OPTS} /></div></div>
      </div>
      <div className="mr-chart-grid">
        <div className="mr-card"><h3>Top Booked Rooms</h3><div className="mr-chart-wrap" style={{ height: 280 }}><Bar data={roomChart} options={{ ...BAR_OPTS, indexAxis: 'y', scales: { x: { grid: { color: '#f0f0f0' }, beginAtZero: true }, y: { grid: { display: false }, ticks: { font: { size: 11 } } } } }} /></div></div>
        <div className="mr-card"><h3>Peak Booking Hours</h3><div className="mr-chart-wrap" style={{ height: 280 }}><Bar data={hourChart} options={BAR_OPTS} /></div></div>
      </div>
      <div className="mr-card mr-card-full">
        <div className="mr-card-header">
          <h3>Room Utilization Details</h3>
          <button className="mr-btn-download" onClick={handleDownload}><MdDownload /> Download CSV</button>
        </div>
        <div className="mr-table-wrap">
          <table className="mr-table">
            <thead><tr><th>#</th><th>Room Name</th><th>Type</th><th>Location</th><th>Tower</th><th>Floor</th><th>Bookings</th></tr></thead>
            <tbody>
              {(data.topRooms || []).map((r, i) => (
                <tr key={i}>
                  <td>{i + 1}</td><td><strong>{r.roomName}</strong></td>
                  <td><span className="mr-badge mr-badge-type">{r.roomType}</span></td>
                  <td>{r.location}</td><td>{r.tower}</td><td>{r.floor}</td><td><strong>{r.bookings}</strong></td>
                </tr>
              ))}
              {!(data.topRooms || []).length && <tr><td colSpan="7" className="mr-td-empty">No room data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPDESK TAB
// ─────────────────────────────────────────────────────────────────────────────
function HelpdeskTab({ data }) {
  if (!data || Object.keys(data).length === 0)
    return <div className="mr-empty">No helpdesk data available for this period.</div>;

  const prev = data.prevMonth || {};

  const yearChart = {
    labels: (data.yearTrend || []).map(r => r.month),
    datasets: [
      { label: 'Resolved/Closed', data: (data.yearTrend || []).map(r => r.resolved), backgroundColor: '#4CAF50', borderRadius: 4 },
      { label: 'Open',            data: (data.yearTrend || []).map(r => r.open),     backgroundColor: '#2196F3', borderRadius: 4 },
    ],
  };

  const dailyChart = {
    labels: (data.dailyTrend || []).map(r => r.day),
    datasets: [
      { label: 'Created',  data: (data.dailyTrend || []).map(r => r.created),  borderColor: '#704493', backgroundColor: 'rgba(112,68,147,0.1)', fill: true, tension: 0.4, pointRadius: 3 },
      { label: 'Resolved', data: (data.dailyTrend || []).map(r => r.resolved), borderColor: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.08)', fill: false, tension: 0.4, pointRadius: 3 },
    ],
  };

  const statusEntries = Object.entries(data.statusBreakdown || {});
  const statusChart = { labels: statusEntries.map(([k]) => k), datasets: [{ data: statusEntries.map(([, v]) => v), backgroundColor: statusEntries.map(([k]) => STATUS_COLORS[k] || '#9E9E9E'), borderWidth: 2, borderColor: '#fff' }] };

  const priorityEntries = Object.entries(data.priorityBreakdown || {});
  const priorityChart = { labels: priorityEntries.map(([k]) => k), datasets: [{ data: priorityEntries.map(([, v]) => v), backgroundColor: priorityEntries.map(([k]) => STATUS_COLORS[k] || '#9E9E9E'), borderWidth: 2, borderColor: '#fff' }] };

  const typeEntries = Object.entries(data.requestTypeBreakdown || {});
  const typeChart = { labels: typeEntries.map(([k]) => k.replace('_', ' ')), datasets: [{ data: typeEntries.map(([, v]) => v), backgroundColor: ['#704493', '#2196F3'], borderWidth: 2, borderColor: '#fff' }] };

  const catEntries = Object.entries(data.categoryBreakdown || {}).sort(([, a], [, b]) => b - a).slice(0, 10);
  const catChart = { labels: catEntries.map(([k]) => k), datasets: [{ label: 'Tickets', data: catEntries.map(([, v]) => v), backgroundColor: CHART_COLORS.slice(0, catEntries.length), borderRadius: 4 }] };

  const locEntries = Object.entries(data.locationBreakdown || {}).sort(([, a], [, b]) => b - a);
  const locChart = { labels: locEntries.map(([k]) => k), datasets: [{ label: 'Tickets', data: locEntries.map(([, v]) => v), backgroundColor: '#704493', borderRadius: 4 }] };

  const handleDownload = () => {
    const rows = Object.entries(data.categoryBreakdown || {}).sort(([, a], [, b]) => b - a).map(([cat, count]) => ({ category: cat, tickets: count, pct: data.totalTickets > 0 ? Math.round(count * 100 / data.totalTickets) : 0 }));
    downloadCSV(`helpdesk-report-${data.period?.replace(' ', '-')}.csv`, rows, ['category', 'tickets', 'pct']);
  };

  return (
    <div className="mr-tab-content">
      <UtilizationBanner
        rate={data.utilizationRate ?? 0}
        headline="Helpdesk Module Utilization"
        detail={`${data.uniqueRequesters ?? 0} of ${data.totalRegisteredUsers ?? '—'} registered employees raised at least one ticket in ${data.period}`}
        secondaryRate={data.resolutionRate ?? 0}
        secondaryLabel="resolution rate (resolved + closed / total tickets)"
      />
      <div className="mr-kpi-grid">
        <KpiCard label="Total Tickets"   value={data.totalTickets}             prev={prev.totalTickets}  color="#704493" icon={<MdSupportAgent />} />
        <KpiCard label="Resolution Rate" value={`${data.resolutionRate ?? 0}%`} sub={prev.resolutionRate !== undefined ? `Prev: ${prev.resolutionRate}%` : ''} color="#4CAF50" icon={<MdTrendingUp />} />
        <KpiCard label="Avg Resolution"  value={`${data.avgResolutionHours ?? 0}h`} color="#FF9800"    icon={<MdCalendarToday />} />
        <KpiCard label="Open Tickets"    value={data.openTickets}              prev={prev.open}          color="#2196F3" icon={<MdTrendingDown />} />
      </div>
      <div className="mr-kpi-grid mr-kpi-grid-4">
        <div className="mr-stat-chip mr-chip-blue">   <span>{data.openTickets}</span>     <small>Open</small></div>
        <div className="mr-stat-chip mr-chip-green">  <span>{data.resolvedTickets}</span> <small>Resolved</small></div>
        <div className="mr-stat-chip mr-chip-grey">   <span>{data.closedTickets}</span>   <small>Closed</small></div>
        <div className="mr-stat-chip mr-chip-purple"> <span>{data.totalTickets}</span>    <small>Total</small></div>
      </div>
      <div className="mr-card mr-card-full">
        <h3>Year Overview — {data.year}</h3>
        <div className="mr-chart-wrap" style={{ height: 260 }}>
          <Bar data={yearChart} options={{ ...BAR_OPTS, scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, beginAtZero: true, grid: { color: '#f0f0f0' } } } }} />
        </div>
      </div>
      <div className="mr-card mr-card-full">
        <h3>Daily Trend — {data.period}</h3>
        <div className="mr-chart-wrap" style={{ height: 230 }}>
          <Line data={dailyChart} options={BAR_OPTS} />
        </div>
      </div>
      <div className="mr-chart-grid mr-chart-grid-3">
        <div className="mr-card"><h3>By Status</h3><div className="mr-chart-wrap" style={{ height: 240 }}><Doughnut data={statusChart} options={DOUGHNUT_OPTS} /></div></div>
        <div className="mr-card"><h3>By Priority</h3><div className="mr-chart-wrap" style={{ height: 240 }}><Doughnut data={priorityChart} options={DOUGHNUT_OPTS} /></div></div>
        <div className="mr-card"><h3>By Request Type</h3><div className="mr-chart-wrap" style={{ height: 240 }}><Doughnut data={typeChart} options={DOUGHNUT_OPTS} /></div></div>
      </div>
      <div className="mr-chart-grid">
        <div className="mr-card"><h3>By Category</h3><div className="mr-chart-wrap" style={{ height: 280 }}><Bar data={catChart} options={{ ...BAR_OPTS, indexAxis: 'y', scales: { x: { grid: { color: '#f0f0f0' }, beginAtZero: true }, y: { grid: { display: false }, ticks: { font: { size: 11 } } } } }} /></div></div>
        <div className="mr-card"><h3>By Location</h3><div className="mr-chart-wrap" style={{ height: 280 }}><Bar data={locChart} options={{ ...BAR_OPTS, indexAxis: 'y', scales: { x: { grid: { color: '#f0f0f0' }, beginAtZero: true }, y: { grid: { display: false }, ticks: { font: { size: 11 } } } } }} /></div></div>
      </div>
      <div className="mr-card mr-card-full">
        <div className="mr-card-header">
          <h3>Category Breakdown</h3>
          <button className="mr-btn-download" onClick={handleDownload}><MdDownload /> Download CSV</button>
        </div>
        <div className="mr-table-wrap">
          <table className="mr-table">
            <thead><tr><th>#</th><th>Category</th><th>Tickets</th><th>% of Total</th></tr></thead>
            <tbody>
              {Object.entries(data.categoryBreakdown || {}).sort(([, a], [, b]) => b - a).map(([cat, count], i) => (
                <tr key={i}>
                  <td>{i + 1}</td><td><strong>{cat}</strong></td><td>{count}</td>
                  <td>
                    <div className="mr-progress-wrap">
                      <div className="mr-progress-bar" style={{ width: `${data.totalTickets > 0 ? Math.round(count * 100 / data.totalTickets) : 0}%` }} />
                      <span>{data.totalTickets > 0 ? Math.round(count * 100 / data.totalTickets) : 0}%</span>
                    </div>
                  </td>
                </tr>
              ))}
              {!Object.keys(data.categoryBreakdown || {}).length && <tr><td colSpan="4" className="mr-td-empty">No category data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// JOB MANAGEMENT TAB
// ─────────────────────────────────────────────────────────────────────────────
function JobTab({ data }) {
  if (!data || Object.keys(data).length === 0)
    return <div className="mr-empty">No job management data available for this period.</div>;

  const prev = data.prevMonth || {};

  const yearChart = {
    labels: (data.yearTrend || []).map(r => r.month),
    datasets: [
      { label: 'Sourced', data: (data.yearTrend || []).map(r => r.sourced), backgroundColor: '#2196F3', borderRadius: 4 },
      { label: 'Offered', data: (data.yearTrend || []).map(r => r.offered), backgroundColor: '#FF9800', borderRadius: 4 },
      { label: 'Joined',  data: (data.yearTrend || []).map(r => r.joined),  backgroundColor: '#4CAF50', borderRadius: 4 },
    ],
  };

  const dailyChart = {
    labels: (data.dailyTrend || []).map(r => r.day),
    datasets: [
      { label: 'Sourced', data: (data.dailyTrend || []).map(r => r.sourced), borderColor: '#2196F3', backgroundColor: 'rgba(33,150,243,0.1)', fill: true, tension: 0.4, pointRadius: 3 },
      { label: 'Joined',  data: (data.dailyTrend || []).map(r => r.joined),  borderColor: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.08)', fill: false, tension: 0.4, pointRadius: 3 },
    ],
  };

  const stageEntries = Object.entries(data.stageBreakdown || {});
  const stageChart = { labels: stageEntries.map(([k]) => k), datasets: [{ data: stageEntries.map(([, v]) => v), backgroundColor: stageEntries.map(([k]) => STATUS_COLORS[k] || CHART_COLORS[stageEntries.findIndex(([ek]) => ek === k) % CHART_COLORS.length]), borderWidth: 2, borderColor: '#fff' }] };

  const sourceEntries = Object.entries(data.sourceBreakdown || {});
  const sourceChart = { labels: sourceEntries.map(([k]) => k), datasets: [{ data: sourceEntries.map(([, v]) => v), backgroundColor: CHART_COLORS.slice(0, sourceEntries.length), borderWidth: 2, borderColor: '#fff' }] };

  const verticalEntries = Object.entries(data.verticalBreakdown || {});
  const verticalChart = { labels: verticalEntries.map(([k]) => k), datasets: [{ label: 'Candidates', data: verticalEntries.map(([, v]) => v), backgroundColor: CHART_COLORS.slice(0, verticalEntries.length), borderRadius: 4 }] };

  const topRecruiters = (data.topRecruiters || []).slice(0, 10);
  const recruiterChart = {
    labels: topRecruiters.map(r => r.recruiter.length > 18 ? r.recruiter.substring(0, 18) + '…' : r.recruiter),
    datasets: [
      { label: 'Sourced', data: topRecruiters.map(r => r.sourced), backgroundColor: '#2196F3', borderRadius: 4 },
      { label: 'Joined',  data: topRecruiters.map(r => r.joined),  backgroundColor: '#4CAF50', borderRadius: 4 },
    ],
  };

  const handleDownload = () => {
    downloadCSV(`job-report-${data.period?.replace(' ', '-')}.csv`, data.topRecruiters || [], ['recruiter', 'sourced', 'joined']);
  };

  return (
    <div className="mr-tab-content">
      <UtilizationBanner
        rate={data.conversionRate ?? 0}
        headline="Recruitment Pipeline Conversion Rate"
        detail={`${data.joined ?? 0} candidates joined out of ${data.totalCandidates ?? 0} sourced in ${data.period}`}
        secondaryRate={data.offerAcceptanceRate ?? 0}
        secondaryLabel="offer acceptance rate (accepted / released)"
      />
      <div className="mr-kpi-grid">
        <KpiCard label="Total Candidates" value={data.totalCandidates} prev={prev.totalCandidates} color="#704493" icon={<MdWork />} />
        <KpiCard label="Joined"           value={data.joined}          prev={prev.joined}          color="#4CAF50" icon={<MdTrendingUp />} />
        <KpiCard label="In Offer Stage"   value={data.offered}                                      color="#FF9800" icon={<MdCalendarToday />} />
        <KpiCard label="Conversion Rate"  value={`${data.conversionRate ?? 0}%`} sub={prev.conversionRate !== undefined ? `Prev: ${prev.conversionRate}%` : ''} color="#2196F3" icon={<MdTrendingUp />} />
      </div>

      <div className="mr-card mr-card-full">
        <h3>Year Overview — {data.year}</h3>
        <div className="mr-chart-wrap" style={{ height: 260 }}>
          <Bar data={yearChart} options={BAR_OPTS} />
        </div>
      </div>

      <div className="mr-card mr-card-full">
        <h3>Daily Trend — {data.period}</h3>
        <div className="mr-chart-wrap" style={{ height: 230 }}>
          <Line data={dailyChart} options={BAR_OPTS} />
        </div>
      </div>

      <div className="mr-chart-grid mr-chart-grid-3">
        <div className="mr-card"><h3>By Stage</h3><div className="mr-chart-wrap" style={{ height: 240 }}><Doughnut data={stageChart} options={DOUGHNUT_OPTS} /></div></div>
        <div className="mr-card"><h3>By Source</h3><div className="mr-chart-wrap" style={{ height: 240 }}><Doughnut data={sourceChart} options={DOUGHNUT_OPTS} /></div></div>
        <div className="mr-card"><h3>By Vertical</h3><div className="mr-chart-wrap" style={{ height: 240 }}><Bar data={verticalChart} options={{ ...BAR_OPTS, indexAxis: 'y', scales: { x: { grid: { color: '#f0f0f0' }, beginAtZero: true }, y: { grid: { display: false }, ticks: { font: { size: 11 } } } } }} /></div></div>
      </div>

      <div className="mr-card mr-card-full">
        <div className="mr-card-header">
          <h3>Recruiter Performance</h3>
          <button className="mr-btn-download" onClick={handleDownload}><MdDownload /> Download CSV</button>
        </div>
        <div className="mr-chart-wrap" style={{ height: 280 }}>
          <Bar data={recruiterChart} options={{ ...BAR_OPTS, indexAxis: 'y', scales: { x: { grid: { color: '#f0f0f0' }, beginAtZero: true }, y: { grid: { display: false }, ticks: { font: { size: 11 } } } } }} />
        </div>
        <div className="mr-table-wrap" style={{ marginTop: 16 }}>
          <table className="mr-table">
            <thead><tr><th>#</th><th>Recruiter</th><th>Sourced</th><th>Joined</th><th>Conversion</th></tr></thead>
            <tbody>
              {(data.topRecruiters || []).map((r, i) => (
                <tr key={i}>
                  <td>{i + 1}</td><td><strong>{r.recruiter}</strong></td>
                  <td>{r.sourced}</td><td><span className="mr-badge mr-badge-am">{r.joined}</span></td>
                  <td>{r.sourced > 0 ? Math.round(r.joined * 100 / r.sourced) : 0}%</td>
                </tr>
              ))}
              {!(data.topRecruiters || []).length && <tr><td colSpan="5" className="mr-td-empty">No recruiter data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ID CARD HISTORY TAB
// ─────────────────────────────────────────────────────────────────────────────
function IdCardTab({ data }) {
  if (!data || Object.keys(data).length === 0)
    return <div className="mr-empty">No ID card history data available for this period.</div>;

  const STATUS_LABELS = {
    REGISTERED: 'Registered', SENT_FOR_PRINT: 'Sent for Print',
    RECEIVED_FROM_VENDOR: 'Received from Vendor', DELIVERED: 'Delivered',
    YET_TO_REGISTER: 'Yet to Register', UNKNOWN: 'Unknown',
  };
  const STATUS_COLORS_MAP = {
    REGISTERED: '#2196F3', SENT_FOR_PRINT: '#FF9800',
    RECEIVED_FROM_VENDOR: '#9C27B0', DELIVERED: '#4CAF50',
    YET_TO_REGISTER: '#F44336', UNKNOWN: '#607D8B',
  };

  const byStatusEntries = Object.entries(data.byStatus || {});
  const statusChart = {
    labels: byStatusEntries.map(([k]) => STATUS_LABELS[k] || k),
    datasets: [{
      data: byStatusEntries.map(([, v]) => v),
      backgroundColor: byStatusEntries.map(([k]) => STATUS_COLORS_MAP[k] || '#607D8B'),
      borderWidth: 2, borderColor: '#fff',
    }],
  };

  const trendChart = {
    labels: (data.yearTrend || []).map(r => r.month),
    datasets: [{
      label: 'ID Card Requests',
      data: (data.yearTrend || []).map(r => r.count),
      borderColor: '#704493', backgroundColor: 'rgba(112,68,147,0.12)',
      fill: true, tension: 0.4, pointRadius: 3,
    }],
  };

  const handleDownload = () => {
    downloadCSV(
      `idcard-report-${data.period?.replace(/ /g, '-')}.csv`,
      data.topEmployees || [],
      ['employeeId', 'employeeName', 'employeeEmail', 'count', 'latestStatus']
    );
  };

  return (
    <div className="mr-tab-content">
      <UtilizationBanner
        rate={data.utilizationRate ?? 0}
        headline="ID Card History Module Utilization"
        detail={`${data.uniqueEmployees ?? 0} out of ${data.totalRegisteredUsers ?? '—'} registered employees had ID card requests in ${data.period}`}
      />
      <div className="mr-kpi-grid">
        <KpiCard label="Requests (Period)" value={data.totalPeriod}  color="#704493" icon={<MdBadge />} />
        <KpiCard label="All-Time Total"    value={data.totalAllTime} color="#2196F3" icon={<MdTrendingUp />} />
        <KpiCard label="Unique Employees"  value={data.uniqueEmployees} color="#4CAF50" icon={<MdSupportAgent />} />
      </div>

      <div className="mr-chart-grid">
        <div className="mr-card">
          <h3>By Status</h3>
          <div className="mr-chart-wrap" style={{ height: 260 }}>
            <Doughnut data={statusChart} options={DOUGHNUT_OPTS} />
          </div>
        </div>
        <div className="mr-card">
          <h3>Monthly Trend — Past 12 Months</h3>
          <div className="mr-chart-wrap" style={{ height: 260 }}>
            <Line data={trendChart} options={BAR_OPTS} />
          </div>
        </div>
      </div>

      <div className="mr-card mr-card-full">
        <div className="mr-card-header">
          <h3>Top Employees by Request Count</h3>
          <button className="mr-btn-download" onClick={handleDownload}><MdDownload /> Download CSV</button>
        </div>
        <div className="mr-table-wrap">
          <table className="mr-table">
            <thead>
              <tr><th>#</th><th>Emp ID</th><th>Name</th><th>Email</th><th>Requests</th><th>Latest Status</th></tr>
            </thead>
            <tbody>
              {(data.topEmployees || []).map((e, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td><code className="mr-empid">{e.employeeId}</code></td>
                  <td><strong>{e.employeeName}</strong></td>
                  <td>{e.employeeEmail}</td>
                  <td><strong style={{ color: '#704493' }}>{e.count}</strong></td>
                  <td><span className="mr-badge" style={{ backgroundColor: STATUS_COLORS_MAP[e.latestStatus] || '#607D8B', color: '#fff' }}>{STATUS_LABELS[e.latestStatus] || e.latestStatus}</span></td>
                </tr>
              ))}
              {!(data.topEmployees || []).length && <tr><td colSpan="6" className="mr-td-empty">No data for this period</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function firstOfMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export default function MonthlyReports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initTab = searchParams.get('tab') || 'vehicle';
  const [activeTab,  setActiveTab]  = useState(initTab);
  const [startDate,  setStartDate]  = useState(firstOfMonthStr);
  const [endDate,    setEndDate]    = useState(todayStr);
  const [location,   setLocation]   = useState('');
  const [locations,  setLocations]  = useState([]);
  const [data,       setData]       = useState({});
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  // Load location dropdown once
  useEffect(() => {
    monthlyReportsApi.getLocations().then(locs => setLocations(locs)).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let result;
      if (activeTab === 'vehicle') {
        result = await monthlyReportsApi.getVehicleReport(startDate, endDate, location || null);
      } else if (activeTab === 'workspace') {
        result = await monthlyReportsApi.getWorkspaceReport(startDate, endDate, location || null);
      } else if (activeTab === 'helpdesk') {
        result = await monthlyReportsApi.getHelpdeskReport(startDate, endDate, location || null);
      } else if (activeTab === 'idcard') {
        result = await monthlyReportsApi.getIdCardReport(startDate, endDate, location || null);
      } else {
        result = await monthlyReportsApi.getJobReport(startDate, endDate, location || null);
      }
      setData(prev => ({ ...prev, [activeTab]: result }));
    } catch (e) {
      console.error('[MonthlyReports]', e);
      setError(e?.message || 'Failed to load report data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, startDate, endDate, location]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const switchTab = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const tabData = data[activeTab] || {};
  const showLocationFilter = true; // location filter available on all tabs


  return (
    <div className="monthly-reports">
      <div className="mr-header">
        <div className="mr-header-left">
          <h1>Monthly Reports</h1>
          <p>Track how much each module is being utilized by your employees every month</p>
        </div>
        <button className="mr-btn-refresh" onClick={fetchData} disabled={loading}>
          <MdRefresh className={loading ? 'mr-spin' : ''} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div className="mr-module-tabs">
        {TABS.map(tab => (
          <button key={tab.id} className={`mr-module-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => switchTab(tab.id)}>
            <span className="mr-tab-icon">{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="mr-filter-bar">
        <MdFilterList className="mr-filter-icon" />
        <div className="mr-filter-group">
          <label>From</label>
          <input type="date" value={startDate} max={endDate} onChange={e => setStartDate(e.target.value)} className="mr-date-input" />
        </div>
        <div className="mr-filter-group">
          <label>To</label>
          <input type="date" value={endDate} min={startDate} max={todayStr()} onChange={e => setEndDate(e.target.value)} className="mr-date-input" />
        </div>
        {showLocationFilter && (
          <div className="mr-filter-group">
            <label>Location</label>
            <select value={location} onChange={e => setLocation(e.target.value)}>
              <option value="">All Locations</option>
              {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
            </select>
          </div>
        )}
        <button className="mr-btn-generate" onClick={fetchData} disabled={loading}>
          Generate Report
        </button>
        {tabData.period && (
          <span className="mr-period-badge">
            <MdCalendarToday /> {tabData.period}
          </span>
        )}
      </div>

      {error && (
        <div className="mr-error">
          <strong>Could not load report:</strong> {error}
          <br/><small>Please ensure the backend server is running and refresh the page.</small>
        </div>
      )}

      {loading && (
        <div className="mr-loading">
          <div className="mr-skeleton mr-skeleton-kpi" />
          <div className="mr-skeleton mr-skeleton-chart" />
          <div className="mr-skeleton mr-skeleton-chart" />
        </div>
      )}

      {!loading && !error && (
        <>
          {activeTab === 'vehicle'   && <VehicleTab   data={tabData} />}
          {activeTab === 'workspace' && <WorkspaceTab data={tabData} />}
          {activeTab === 'helpdesk'  && <HelpdeskTab  data={tabData} />}
          {activeTab === 'job'       && <JobTab       data={tabData} />}
          {activeTab === 'idcard'    && <IdCardTab    data={tabData} />}
        </>
      )}
    </div>
  );
}
